import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Zap } from 'lucide-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useUpdateOpenaiMutation } from '@/redux/service/configuration'
import toast from 'react-hot-toast'

interface OpenaiUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  initialToken?: string
}

const OpenaiUpdateModal: React.FC<OpenaiUpdateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialToken = '',
}) => {
  const [updateOpenai] = useUpdateOpenaiMutation()

  const formik = useFormik({
    initialValues: {
      token: initialToken,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      token: Yup.string().required('OpenAI API token is required'),
    }),
    onSubmit: async values => {
      try {
        await updateOpenai({ open_ai: values.token }).unwrap()
        toast.success('OpenAI token updated successfully')
        onSuccess?.()
        onClose()
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Failed to update OpenAI token Please try again.'
        toast.error(message)
      }
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[425px] overflow-hidden p-0 rounded-3xl border-none shadow-2xl'>
        <DialogHeader className='px-6 pt-8 pb-4 bg-gradient-to-b from-muted/50 to-card'>
          <div className='w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 mx-auto'>
            <Zap className='w-6 h-6 text-primary' />
          </div>
          <DialogTitle className='text-2xl font-bold text-center text-foreground'>
            Update OpenAI Token
          </DialogTitle>
          <DialogDescription className='text-center text-muted-foreground px-4'>
            Update your OpenAI API key to continue using AI-powered insights.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className='px-8 py-6 space-y-6'>
          <div className='space-y-2'>
            <Label
              htmlFor='token'
              className='text-sm font-semibold text-foreground'
            >
              API Token <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='token'
              name='token'
              type='password'
              placeholder='Enter your OpenAI API token'
              value={formik.values.token}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`h-12 rounded-xl border-border bg-muted focus:bg-background transition-all duration-200 ${
                formik.touched.token && formik.errors.token
                  ? 'border-destructive focus:ring-destructive/10'
                  : 'focus:border-primary focus:ring-primary/10'
              }`}
            />
            {formik.touched.token && formik.errors.token && (
              <p className='text-[13px] text-destructive font-medium mt-1'>
                {formik.errors.token}
              </p>
            )}
          </div>

          <div className='flex items-center gap-3 pt-4'>
            <Button
              type='button'
              variant='ghost'
              onClick={onClose}
              className='flex-1 h-12 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted font-semibold transition-all'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={formik.isSubmitting}
              className='flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm hover:shadow-md transition-all'
            >
              {formik.isSubmitting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default OpenaiUpdateModal
